import * as React from "react";
import { cn } from "@/lib/utils";
import { Button } from "./button";

interface Column<Row> {
  key: string;
  header: string;
  /** Identifiers, codes, and logs use Geist Mono. */
  mono?: boolean;
  cell?: (row: Row) => React.ReactNode;
}

interface DataTableProps<Row> {
  caption: string;
  columns: Column<Row>[];
  rows: Row[];
  getRowId: (row: Row) => string;
  pageSize?: number;
  className?: string;
}

/**
 * A table with page controls. Status belongs in a labelled badge supplied by the caller, never as
 * colour on the row alone. Paging buttons follow the same disabled treatment as every other control.
 */
function DataTable<Row>({
  caption,
  columns,
  rows,
  getRowId,
  pageSize = 10,
  className,
}: DataTableProps<Row>) {
  const [page, setPage] = React.useState(0);
  const [compact, setCompact] = React.useState(false);
  const pageCount = Math.max(1, Math.ceil(rows.length / pageSize));
  const current = Math.min(page, pageCount - 1);
  const start = current * pageSize;
  const visible = rows.slice(start, start + pageSize);
  const from = rows.length === 0 ? 0 : start + 1;
  const to = start + visible.length;

  return (
    <div className={cn("flex flex-col gap-3", className)}>
      <div className="flex items-center justify-end gap-2">
        <span className="text-xs text-muted-foreground">Table density</span>
        <Button
          type="button"
          variant="secondary"
          size="sm"
          aria-label="Toggle table density"
          aria-pressed={compact}
          onClick={() => setCompact((value) => !value)}
        >
          {compact ? "Compact" : "Comfortable"}
        </Button>
      </div>
      <div className="overflow-x-auto rounded-lg border bg-card">
        <table className="w-full border-collapse text-sm">
          <caption className="sr-only">{caption}</caption>
          <thead>
            <tr>
              {columns.map((column) => (
                <th
                  key={column.key}
                  scope="col"
                  className={cn(
                    "sticky top-0 z-10 h-10 border-b bg-card text-left text-[0.8125rem] font-medium whitespace-nowrap text-muted-foreground",
                    compact ? "px-2" : "px-5",
                  )}
                >
                  {column.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {visible.map((row) => (
              <tr key={getRowId(row)} className="group hover:bg-muted/50">
                {columns.map((column) => (
                  <td
                    key={column.key}
                    className={cn(
                      "border-b align-middle whitespace-nowrap group-last:border-b-0",
                      compact ? "px-2 py-2" : "px-5 py-3",
                      column.mono && "font-mono text-[0.8125rem]",
                    )}
                  >
                    {column.cell
                      ? column.cell(row)
                      : String((row as Record<string, unknown>)[column.key] ?? "")}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="flex items-center justify-between gap-3">
        <p className="text-[0.8125rem] text-muted-foreground tabular-nums">
          {from}–{to} of {rows.length}
        </p>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            disabled={current === 0}
            onClick={() => setPage(current - 1)}
          >
            Previous page
          </Button>
          <Button
            variant="outline"
            size="sm"
            disabled={current >= pageCount - 1}
            onClick={() => setPage(current + 1)}
          >
            Next page
          </Button>
        </div>
      </div>
    </div>
  );
}

export { DataTable };
export type { Column, DataTableProps };
